/**
 * Thinccc Design System - Card Component
 *
 * Base card container with variants matching Figma design
 */
import React from "react";

import PropTypes from "prop-types";

// ===========================================
// VARIANT STYLES
// ===========================================
const variantStyles = {
  // Default elevated card
  elevated: `
    bg-background-cloudburst
    shadow-card
    hover:shadow-card-hover
    transition-shadow duration-normal ease-standard
  `,
  // Outlined card
  outlined: `
    bg-transparent
    border border-background-biscay
    hover:border-background-astronaut
    transition-colors duration-normal ease-standard
  `,
  // Filled card (solid background)
  filled: `
    bg-background-midnight
  `,
  // Glass effect card
  glass: `
    bg-background-cloudburst/60
    backdrop-blur-md
    border border-white/10
  `,
  // Interactive card (for clickable items)
  interactive: `
    bg-background-cloudburst
    shadow-card
    hover:shadow-card-hover hover:bg-background-biscay
    active:bg-background-astronaut
    cursor-pointer
    transition-all duration-normal ease-standard
  `,
};

// ===========================================
// PADDING SIZES
// ===========================================
const paddingStyles = {
  none: "",
  sm: "p-3",
  md: "p-4",
  lg: "p-5",
  xl: "p-6",
};

// ===========================================
// BORDER RADIUS
// ===========================================
const radiusStyles = {
  sm: "rounded-lg",
  md: "rounded-xl",
  lg: "rounded-2xl",
  xl: "rounded-3xl",
};

// ===========================================
// CARD COMPONENT
// ===========================================
const Card = React.forwardRef(
  (
    {
      children,
      variant = "elevated",
      padding = "md",
      radius = "lg",
      className = "",
      onClick,
      as: Component = "div",
      ...props
    },
    ref,
  ) => {
    const cardClasses = `
    ${variantStyles[variant]}
    ${paddingStyles[padding]}
    ${radiusStyles[radius]}
    ${className}
  `
      .replace(/\s+/g, " ")
      .trim();

    return (
      <Component ref={ref} className={cardClasses} onClick={onClick} {...props}>
        {children}
      </Component>
    );
  },
);

Card.displayName = "Card";

// ===========================================
// CARD SUB-COMPONENTS
// ===========================================

// Card Header
const CardHeader = ({ children, className = "", ...props }) => (
  <div
    className={`flex items-center justify-between mb-4 ${className}`}
    {...props}
  >
    {children}
  </div>
);

CardHeader.displayName = "CardHeader";

// Card Title
const CardTitle = ({
  children,
  className = "",
  as: Component = "h3",
  ...props
}) => (
  <Component
    className={`text-lg font-medium text-text-lv0 ${className}`}
    {...props}
  >
    {children}
  </Component>
);

CardTitle.displayName = "CardTitle";

// Card Content
const CardContent = ({ children, className = "", ...props }) => (
  <div className={`text-text-lv2 ${className}`} {...props}>
    {children}
  </div>
);

CardContent.displayName = "CardContent";

// Card Footer
const CardFooter = ({ children, className = "", ...props }) => (
  <div
    className={`flex items-center justify-end gap-3 mt-4 pt-4 border-t border-background-biscay ${className}`}
    {...props}
  >
    {children}
  </div>
);

CardFooter.displayName = "CardFooter";

// Card Media (for images)
const CardMedia = ({
  src,
  alt,
  className = "",
  aspectRatio = "16/9",
  ...props
}) => (
  <div
    className={`overflow-hidden rounded-lg -mx-4 -mt-4 mb-4 first:-mt-5 first:-mx-5 ${className}`}
    style={{ aspectRatio }}
    {...props}
  >
    <img src={src} alt={alt} className="w-full h-full object-cover" />
  </div>
);

CardMedia.displayName = "CardMedia";

// ===========================================
// PROP TYPES
// ===========================================
Card.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf([
    "elevated",
    "outlined",
    "filled",
    "glass",
    "interactive",
  ]),
  padding: PropTypes.oneOf(["none", "sm", "md", "lg", "xl"]),
  radius: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  className: PropTypes.string,
  onClick: PropTypes.func,
  as: PropTypes.elementType,
};

CardHeader.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

CardTitle.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  as: PropTypes.elementType,
};

CardContent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

CardFooter.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

CardMedia.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  aspectRatio: PropTypes.string,
};

// Attach sub-components
Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Content = CardContent;
Card.Footer = CardFooter;
Card.Media = CardMedia;

export default Card;
